import { Button, VersionField, VersionsTable } from '../../atoms';
import { ADMIN, FULLADMIN } from '../../../constants/roles';
import { ICanary, Status } from '../../../types';
import React, { useState } from 'react';

import { useGlobalContext } from '../../context';
import useStyles from './CanaryManagement.styles';

interface ICanaryManagement {
  startCanaryHandler: (version: string, errorInputFormat: boolean) => void;
  fullStartCanaryHandler: (uid: string) => void;
  deployToGAHandler: (uid: string) => void;
  goingToGAHandler: (uid: string) => void;
  terminateHandler: (uid: string) => void;
  canary?: ICanary;
  isCanaryManagementAvailable: boolean;
}

export const CanaryManagement: React.FC<ICanaryManagement> = ({
  startCanaryHandler,
  fullStartCanaryHandler,
  goingToGAHandler,
  deployToGAHandler,
  terminateHandler,
  canary,
  isCanaryManagementAvailable,
}) => {
  const classes = useStyles();
  const { userdata } = useGlobalContext();
  const { appConfig } = useGlobalContext();
  const [canaryVersion, setNewCanaryVersion] = useState<string>();
  const [errorInputFormat, setErrorInputFormat] = useState<boolean>();
  const [isStartCanaryEnabled, setStartCanaryEnabled] =
    React.useState<boolean>(false);

  const changeInputVersion = (version: string) => {
    setNewCanaryVersion(version);
    setErrorInputFormat(false);
    if (version.trim() == '') {
      setStartCanaryEnabled(false);
    } else {
      setStartCanaryEnabled(true);
    }
  };

  // eslint-disable-next-line
  const handleStartCanaryClick = () => startCanaryHandler(canaryVersion!, errorInputFormat!);
  // eslint-disable-next-line
  const handleFullStartCanaryClick = () => fullStartCanaryHandler(canary!.id!);
  // eslint-disable-next-line
  const handleGoingToGAClick = () => goingToGAHandler(canary!.id!);
  // eslint-disable-next-line
  const handleDeployToGAClick = () => deployToGAHandler(canary!.id!);
  // eslint-disable-next-line
  const handleTerminateClick = () => terminateHandler(canary!.id!);

  const handleBlur = (event: {
    target: { validity: { patternMismatch: boolean } };
  }) => {
    if (event.target.validity.patternMismatch) {
      setErrorInputFormat(true);
    }
  };

  const renderByStatus = (status: Status) => {
    switch (status) {
      case 'GOING_TO_CANARY':
        return (
          <div className={classes.inputWithButton}>
            {(userdata.roles.includes(ADMIN) ||
              userdata.roles.includes(FULLADMIN)) && (
              <div className={classes.button}>
                <Button onAction={handleFullStartCanaryClick} size="large">
                  Full start Canary
                </Button>
              </div>
            )}
            {(userdata.roles.includes(ADMIN) ||
              userdata.roles.includes(FULLADMIN)) && (
              <div className={classes.button}>
                <Button onAction={handleTerminateClick} size="large">
                  Terminate Canary
                </Button>
              </div>
            )}
          </div>
        );
      case 'ACTIVE':
        return (
          <div className={classes.inputWithButton}>
            {(userdata.roles.includes(ADMIN) ||
              userdata.roles.includes(FULLADMIN)) && (
              <div className={classes.button}>
                <Button onAction={handleGoingToGAClick} size="large">
                  Convert Canary to GA
                </Button>
              </div>
            )}
            {(userdata.roles.includes(ADMIN) ||
              userdata.roles.includes(FULLADMIN)) && (
              <div className={classes.button}>
                <Button onAction={handleTerminateClick} size="large">
                  Terminate Canary
                </Button>
              </div>
            )}
          </div>
        );
      case 'GOING_TO_GA':
        return (
          <div className={classes.inputWithButton}>
            {(userdata.roles.includes(ADMIN) ||
              userdata.roles.includes(FULLADMIN)) && (
              <div className={classes.button}>
                <Button onAction={handleDeployToGAClick} size="large">
                  Complete Canary
                </Button>
              </div>
            )}
            {(userdata.roles.includes(ADMIN) ||
              userdata.roles.includes(FULLADMIN)) && (
              <div className={classes.button}>
                <Button onAction={handleTerminateClick} size="large">
                  Terminate Canary
                </Button>
              </div>
            )}
          </div>
        );
      default:
        return (
          <div className={classes.inputWithButton}>
            {(userdata.roles.includes(ADMIN) ||
              userdata.roles.includes(FULLADMIN)) && (
              <VersionField
                changeInputVersion={changeInputVersion}
                size={'medium'}
                errorInputFormat={errorInputFormat}
                handleBlur={handleBlur}
                inputMask={
                  appConfig.enableCanaryVersionWithAsteriskFeature
                    ? '(\\d{1,3})\\.(\\d{1,3})\\.(\\d{1,3}|\\*)\\.(\\d{1,3}|\\*)'
                    : '(\\d{1,3})\\.(\\d{1,3})\\.(\\d{1,3})\\.(\\d{1,3})'
                }
              />
            )}
            <div className={classes.button}>
              {(userdata.roles.includes(ADMIN) ||
                userdata.roles.includes(FULLADMIN)) && (
                <Button
                  onAction={handleStartCanaryClick}
                  size="medium"
                  disabled={!isStartCanaryEnabled}
                >
                  Start Canary
                </Button>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className={classes.root}>
      {isCanaryManagementAvailable && (
        <div
          className={classes.controlAndStatus}
          data-qa-name="description_text"
        >
          {((userdata.roles.includes(ADMIN) ||
            userdata.roles.includes(FULLADMIN)) &&
            canary?.status &&
            canary.status == 'GOING_TO_GA' && (
              <div className={classes.status}>
                Treat Canary should be installed on all production machines.
                <br />
                Then press the button Complete Canary.
                <br />
                After that the new GA Treat will be launched for all users.
              </div>
            )) ||
            ((userdata.roles.includes(ADMIN) ||
              userdata.roles.includes(FULLADMIN)) &&
              canary?.status &&
              canary.status == 'GOING_TO_CANARY' && (
                <div className={classes.status}>
                  Treat Canary should be installed for all users from the canary
                  list.
                  <br />
                  Then press the button Full canary start.
                  <br />
                  After that Treat Canary will be launched for all users from
                  the canary list.
                </div>
              ))}
          {renderByStatus(canary?.status)}
        </div>
      )}
      <div className={classes.versionsTable}>
        <VersionsTable
          gaVersion={canary?.gaVersion?.version}
          canaryVersion={canary?.canaryVersion?.version}
        />
      </div>
    </div>
  );
};
